/**
 * AlignerShellGenerator — capped-teeth + multi-axis SDF + insertion blockout.
 *
 * Pipeline (insertion axis ≈ +Z after alignment):
 *   1. Align capped + uncapped tooth meshes + trim curve.
 *   2. SDF (multi-axis raycast sign mode — robust against tooth-cap seam coincidence).
 *   3. Re-base the field onto the real tooth: keep the capped solid's sign, but swap
 *      the distance magnitude for distance to the *uncapped* crowns (see below).
 *   4. Morphological close (dilate then erode, 26-connected) and union with source.
 *   5. Insertion-axis undercut blockout (per-column running min from +Z).
 *   6. Shell-band field: negative inside the wall {shellOffset <= d <= shellOffset+thickness}.
 *   7. Gingival trim: clip the band SDF to the crown side of the trim height map (an exact
 *      sub-voxel cut — the rim lands on the trim curve).
 *   8. Marching cubes at iso = 0 (a hollow, open shell — no thicken, no cap deletion).
 *   9. Taubin smooth + inverse align transform back to world space.
 *
 * Capped vs uncapped split: capping (synthetic faces snapped to the gum) exists only
 * to give a watertight volume for a reliable inside/outside sign. The cap must NOT
 * shape the shell — otherwise an unwanted wall forms along the synthetic gingival
 * patch. So the working field takes its *sign* from the capped solid but its *distance*
 * from the uncapped crowns (real tooth surfaces). The shell band then offsets from the
 * real tooth and wraps naturally around its lower edge; the cap region sits far away in
 * this field (large |d|), so no band forms on it. Gap-fill / prune / blockout still run
 * on this field — their inside set is the capped solid, so bridging and undercut
 * blockout behave exactly as before. The gum mesh is never unioned into the SDF.
 */

import { Box3, BufferGeometry, Matrix4, Mesh, Quaternion, Vector3, type Vector3 as Vector3Type } from 'three';
import {
  SDFGenerator,
  VoxelGrid,
  blockoutAlongInsertion,
  closeSDF,
  extractMesh,
  getSdfWorkerPool,
  pruneInsideComponents,
  unionSDF,
} from '@modules/three/common/voxels';
import {
  buildTrimProfile,
  labelShellBandComponents,
  type TrimFieldStats,
  type TrimProfileCurve,
} from './alignerTrimField';
import { applyTrimCut, loopNormal } from './alignerTrimCut';
import { buildLateralThicknessField, buildVariableWallBandGrid } from './alignerThickening';
import { buildBandVoxelDebugLayers, type BandVoxelDebugLayers } from './alignerBandDebug';
import { smoothShellRim, smoothSurface, weldMeshVertices } from './alignerMeshShell';

export type AlignerDebugStage =
  | 'source'
  | 'filled'
  | 'blockout'
  | 'envelope'
  | 'shell'
  | 'final';

/** UI/store request for an aligner generation run. */
export type AlignerShellRequest = {
  jaw: 'upper' | 'lower' | 'both';
  shellOffset: number;
  voxelSize: number;
  thickness: number;
  blockoutStrength: number;
  debugStage?: AlignerDebugStage;
  trimCurve?: Vector3[];
  /** Snap the extracted rim exactly onto the trim curve (see `trimRimClamp`). Default on. */
  trimRimClamp?: boolean;
};

export type AlignerShellResult = {
  surfaceGeometry: BufferGeometry;
  /** Shell-band voxel point clouds (when requested at generation time). */
  voxelDebug?: BandVoxelDebugLayers;
  /** Gingival trim cut-solid (world space) for debug rendering, when requested. */
  debugCutGeometry?: BufferGeometry;
  metadata: {
    grid: {
      counts: { x: number; y: number; z: number };
      voxelSize: number;
      total: number;
      bounds: { min: [number, number, number]; max: [number, number, number] };
    };
    shell: {
      clearance: number;
      thickness: number;
      gapFillSteps: number;
      shellVoxels: number;
    };
    trim: {
      curvePoints: number;
      averageZ: number;
      carvedVoxels: number;
      heightMapCells: number;
      belowCutModified: number;
      insertionOpenModified: number;
      clipRemovedTriangles: number;
    };
    debug: {
      stage: AlignerDebugStage;
      timingsMs: Record<string, number>;
    };
    mesh: { vertexCount: number; triangleCount: number };
  };
};

export type AlignerShellParams = {
  insertionDir: Vector3Type;
  trimCurve: Vector3[];
  /**
   * Per-trim-point extra wall thickness (mm), index-aligned to `trimCurve`. Broadcast up the
   * arch column (outward only) so the selected sections get a thicker wall. Omit / all-zero
   * for a uniform-thickness shell.
   */
  trimThickness?: number[];
  voxelSize?: number;
  gridPadding?: number;
  shellOffset?: number;
  thickness?: number;
  gapFillRadius?: number;
  blockoutStrength?: number;
  /** Taubin smoothing iterations applied to the marching-cubes envelope. */
  smoothIterations?: number;
  /** Gum mesh used for the trim height-map raycast only (never unioned into SDF). */
  gumMesh?: Mesh | null;
  /**
   * Uncapped crown meshes (real tooth surfaces, open at the root — no synthetic gum
   * cap). The shell band's offset distance is measured against these so it follows
   * the real tooth instead of the cap. When omitted/empty, the band falls back to the
   * capped distance (legacy behaviour — wall may follow the cap).
   */
  uncappedTeethMeshes?: Mesh[];
  /**
   * Occlusal lift (mm) of the gingival cut surface toward the crowns — shaves an extra
   * strip above the trim. Defaults to 0: the SDF clip is sub-voxel exact, so the rim lands
   * on the trim curve without any safety lift. Lateral footprint unchanged.
   */
  trimCutLiftMm?: number;
  /**
   * Rim post-clamp (default true): after marching cubes + smoothing, project every vertex that
   * ended up gingival of the trim cut surface back onto it along the occlusal axis. Marching
   * cubes chamfers the wall/cut crease and the Taubin smoother sags the rim — both by up to ~a
   * voxel — so without this the shell hangs below the trim curve, the more so the coarser the
   * voxel. Off = raw voxel-resolution rim.
   */
  trimRimClamp?: boolean;
  trimMargin?: number;
  maxVoxelCount?: number;
  debugStage?: AlignerDebugStage;
  /** Build point-cloud layers for shell-band / barrier / flood debug in the viewer. */
  showBandVoxelDebug?: boolean;
  quiet?: boolean;
};

type ResolvedParams = {
  insertionDir: Vector3;
  voxelSize: number;
  gridPadding: number;
  shellOffset: number;
  thickness: number;
  gapFillRadius: number;
  blockoutStrength: number;
  smoothIterations: number;
  trimCutLiftMm: number;
  trimRimClamp: boolean;
  trimCurve: Vector3[];
  trimMargin: number;
  maxVoxelCount: number;
  debugStage: AlignerDebugStage;
  showBandVoxelDebug: boolean;
  quiet: boolean;
};

type AlignmentContext = {
  alignMatrix: Matrix4;
  inverseAlignMatrix: Matrix4;
};

const DEFAULTS = {
  voxelSize: 0.3,
  // Padding only needs to clear the outer shell band (shellOffset + thickness ≈ 1.1 mm) plus
  // a little slack; the old 4 mm wasted a thick outer shell of far voxels.
  gridPadding: 2,
  shellOffset: 0.25,
  thickness: 0.8,
  gapFillRadius: 0.9,
  blockoutStrength: 0.7,
  smoothIterations: 8,
  trimMargin: 0.3,
  maxVoxelCount: 24_000_000,
};

const INSERTION_AXIS = new Vector3(0, 0, 1);
const LOG_PREFIX = '[Aligner]';

export async function generateAlignerShell(
  cappedTeethMeshes: Mesh[],
  _gumMesh: Mesh | null,
  params: AlignerShellParams,
): Promise<AlignerShellResult> {
  if (!cappedTeethMeshes.length) {
    throw new Error('generateAlignerShell: at least one capped tooth mesh is required.');
  }
  const resolved = resolveParams(params);
  logQuiet = resolved.quiet;

  const uncappedTeethMeshes = params.uncappedTeethMeshes ?? [];
  const alignment = buildInsertionAlignment(resolved.insertionDir);
  const alignedMeshes = alignMeshes(cappedTeethMeshes, alignment.alignMatrix);
  // Perf experiment: the gingival trim-cut already removes everything below the trim curve
  // (including the cap region), so the uncapped rebase is largely redundant. Skipped for now
  // (kept, not deleted) — set to false to re-enable the real-tooth distance rebase.
  const skipUncappedRebase = true;
  const alignedUncapped =
    skipUncappedRebase || !uncappedTeethMeshes.length
      ? []
      : alignMeshes(uncappedTeethMeshes, alignment.alignMatrix);
  const alignedTrim = resolved.trimCurve.map((p) => p.clone().applyMatrix4(alignment.alignMatrix));
  const trimProfile = buildTrimProfile(alignedTrim);

  logAligner('START', {
    debugStage: resolved.debugStage,
    cappedMeshes: cappedTeethMeshes.length,
    uncappedMeshes: alignedUncapped.length,
    params: {
      voxelMm: resolved.voxelSize,
      paddingMm: resolved.gridPadding,
      shellOffsetMm: resolved.shellOffset,
      thicknessMm: resolved.thickness,
      gapFillMm: resolved.gapFillRadius,
      blockoutStrength: resolved.blockoutStrength,
      trimMarginMm: resolved.trimMargin,
    },
    insertionDir: resolved.insertionDir.toArray().map(round),
    trimPointCount: alignedTrim.length,
  });

  const timings: Record<string, number> = {};
  const mark = createTimer(timings);

  // Per-region thickening grows the outer wall outward by up to `maxExtra` mm, so the band /
  // grid headroom must reach that far too.
  const maxExtra = (params.trimThickness ?? []).reduce((m, t) => Math.max(m, t || 0), 0);

  // Outward headroom the grid padding must clear (shell band + gap-fill reach), as a function
  // of voxel size — coarser voxels carry their own slack term.
  const paddingFor = (voxel: number) =>
    Math.max(
      resolved.gridPadding,
      resolved.shellOffset + resolved.thickness + maxExtra + resolved.gapFillRadius + 3 * voxel,
    );

  // Auto-coarsen: at the requested voxel size the grid can blow past the voxel budget (heavy
  // memory/compute, risk of an OOM tab crash). Predict the grid extent from the aligned-mesh
  // bounds and bump the voxel size up just enough to fit under maxVoxelCount BEFORE building the
  // (expensive) SDF, instead of throwing after the fact.
  const fittedVoxel = fitVoxelSizeToBudget(
    unionMeshBounds(alignedMeshes),
    resolved.voxelSize,
    paddingFor,
    resolved.maxVoxelCount,
  );
  if (fittedVoxel > resolved.voxelSize) {
    logAligner('voxel-budget: grid over cap, coarsening voxel', {
      requestedVoxelMm: round(resolved.voxelSize),
      adjustedVoxelMm: round(fittedVoxel),
      maxVoxelCount: resolved.maxVoxelCount,
    });
    resolved.voxelSize = fittedVoxel;
  }

  // Narrow band: exact distance is only ever read inside the shell band (shellOffset +
  // thickness + extra) plus the gap-fill reach (closeSDF moves the surface outward by ≤
  // gapFillRadius). Beyond that the field is used by sign only, so clamp it and skip the query.
  const narrowBandMm =
    resolved.shellOffset + resolved.thickness + maxExtra + resolved.gapFillRadius + 4 * resolved.voxelSize;
  // The grid must clear the outer shell band AND the gap-fill growth with a few extra voxels,
  // or marching cubes can't close the outer wall and caps it flat against the grid face (a
  // boxy slab at the boundary). This floor is the safe minimum; honour a larger requested pad.
  const gridPaddingMm = paddingFor(resolved.voxelSize);
  const sdfGenerator = new SDFGenerator(alignedMeshes);
  // The masked closest-point pass (the SDF hotspot) is fanned out to a worker pool when
  // available; falls back to the synchronous path in tests / when workers are unsupported.
  let sdf = await sdfGenerator.generateAsync(
    {
      cellSize: resolved.voxelSize,
      padding: gridPaddingMm,
      signMode: 'multi-axis-raycast',
      narrowBandMm,
      diagnostics: !resolved.quiet,
    },
    getSdfWorkerPool(),
  );
  sdfGenerator.dispose();
  alignedMeshes.forEach((mesh) => mesh.geometry.dispose());

  // Re-base the field onto the real tooth: keep the capped solid's reliable
  // inside/outside sign, but swap the distance magnitude for distance to the
  // uncapped crowns. The shell band (built later) then offsets from the real
  // tooth and never centers on the synthetic gum cap (which sits at large |d|
  // in this field). Sampled on the capped grid so the two fields stay aligned.
  if (alignedUncapped.length) {
    const uncappedGenerator = new SDFGenerator(alignedUncapped);
    const uncappedDistance = uncappedGenerator.sampleUnsignedDistanceOn(sdf);
    uncappedGenerator.dispose();
    alignedUncapped.forEach((mesh) => mesh.geometry.dispose());
    sdf = rebaseDistanceOntoUncapped(sdf, uncappedDistance);
  }
  mark('source-sdf');
  assertVoxelBudget(sdf, resolved.maxVoxelCount);
  logSdf('source-sdf', sdf, { distanceSource: alignedUncapped.length ? 'uncapped' : 'capped' });

  if (resolved.debugStage === 'source') {
    return finalizeSdf(sdf, {
      filledSDF: sdf,
      trimProfile,
      resolved,
      alignment,
      timings,
      trimStats: emptyStats(0),
      gapFillSteps: 0,
    });
  }

  const gapFillSteps = Math.max(0, Math.ceil(resolved.gapFillRadius / resolved.voxelSize));
  if (gapFillSteps > 0) {
    const closed = closeSDF(sdf, gapFillSteps, '26-connected');
    sdf = unionSDF(sdf, closed);
  }
  // Drop stray "inside" blobs from ambiguous sign votes. Gap-fill has already
  // bridged the arch into one component, so a generous fraction threshold keeps
  // the arch (and any genuinely isolated tooth) while removing the floaters.
  const prune = pruneInsideComponents(sdf, { minVoxels: 64, minFractionOfLargest: 0.05 });
  let filledSDF = prune.grid;
  mark('gap-fill');
  logSdf('filled-sdf', filledSDF, {
    gapFillSteps,
    components: prune.componentCount,
    removedComponents: prune.removedComponents,
    removedVoxels: prune.removedVoxels,
    largestVoxels: prune.largestVoxels,
  });

  if (resolved.debugStage === 'filled') {
    return finalizeSdf(filledSDF, {
      filledSDF,
      trimProfile,
      resolved,
      alignment,
      timings,
      trimStats: emptyStats(0),
      gapFillSteps,
    });
  }

  if (resolved.blockoutStrength > 0) {
    filledSDF = blockoutAlongInsertion(filledSDF, resolved.blockoutStrength);
  }
  mark('blockout');
  logSdf('blockout-sdf', filledSDF, { blockoutStrength: resolved.blockoutStrength });

  if (resolved.debugStage === 'blockout') {
    return finalizeSdf(filledSDF, {
      filledSDF,
      trimProfile,
      resolved,
      alignment,
      timings,
      trimStats: emptyStats(0),
      gapFillSteps,
    });
  }

  // Wall band: the full shell wall {shellOffset ≤ d ≤ shellOffset+thickness}, negative
  // inside the wall and positive on both the inner-cavity and outer sides; marching cubes
  // at iso 0 yields a closed shell of the requested wall thickness. (Previously a thin
  // ~1-voxel band — a near-2D manifold needed so the flood couldn't leak through wall
  // thickness. The height-map trim cut classifies each voxel independently, so the thin
  // trick is gone and the wall now honours `thickness`.)
  // Marching cubes needs ≥ ~3 voxels across the wall or it tears into pinholes. resolveParams
  // guarantees that for the REQUESTED voxel, but the budget auto-coarsening above can raise the
  // voxel past it. Grow the wall OUTWARD to stay resolvable — the inner (fitting) surface stays
  // at shellOffset; the requested thickness is honored whenever the voxel is fine enough.
  const effectiveThickness = Math.max(resolved.thickness, 3 * resolved.voxelSize);
  if (effectiveThickness > resolved.thickness) {
    logAligner('wall-resolution: thickness below 3 voxels at fitted voxel, growing outward', {
      requestedThicknessMm: round(resolved.thickness),
      effectiveThicknessMm: round(effectiveThickness),
      voxelMm: round(resolved.voxelSize),
    });
  }
  const innerIso = resolved.shellOffset;
  const outerIso = resolved.shellOffset + effectiveThickness;
  const shellIso = resolved.shellOffset + effectiveThickness / 2;
  // Cut/thickening axis: the scene's VERTICAL (world +Y) carried into insertion-aligned
  // space. The trim is a HEIGHT cut: the curtain lives over the world XZ plane, so the rim
  // is height-exact from any horizontal view (looking along world Z / X). The insertion
  // axis (world ±Z) is the front-back axis here and must NOT drive the cut — extruding the
  // curtain along it slices across the arch. If the trim loop's plane normal disagrees
  // wildly with world-up (differently oriented scan), fall back to the loop normal so the
  // curtain never cuts across the crowns.
  const worldUpAligned = new Vector3(0, 1, 0).transformDirection(alignment.alignMatrix);
  const trimPlaneNormal = loopNormal(trimProfile.points);
  const upTiltDeg =
    (Math.acos(Math.min(1, Math.abs(trimPlaneNormal.dot(worldUpAligned)))) * 180) / Math.PI;
  const occlusalAxis = upTiltDeg <= 45 ? worldUpAligned : trimPlaneNormal;
  logAligner('cut axis', {
    source: upTiltDeg <= 45 ? 'world-up' : 'trim-plane normal (fallback)',
    axis: occlusalAxis.toArray().map((c) => round(c, 3)),
    trimPlaneTiltVsWorldUpDeg: round(upTiltDeg, 1),
  });
  // Per-region thickening: push the wall's outer iso outward along the arch column where the
  // user assigned extra thickness. Null when nothing is thickened → cheap uniform band.
  const thicknessField = buildLateralThicknessField(
    trimProfile.points,
    params.trimThickness,
    occlusalAxis,
    resolved.voxelSize,
  );
  const bandGrid = thicknessField
    ? buildVariableWallBandGrid(filledSDF, innerIso, outerIso, thicknessField)
    : buildWallBandGrid(filledSDF, shellIso, effectiveThickness / 2);
  mark('shell-band');

  const trimStats: TrimFieldStats = {
    belowCutModified: 0,
    insertionOpenModified: 0,
    gumBelowModified: 0,
    heightMapCells: 0,
  };
  const buildVoxelDebug = (
    grid: VoxelGrid,
    masks?: { barrier: Uint8Array; flooded: Uint8Array },
  ): BandVoxelDebugLayers | undefined => {
    if (!resolved.showBandVoxelDebug) return undefined;
    const { labels } = labelShellBandComponents(grid);
    return buildBandVoxelDebugLayers(grid, {
      barrier: masks?.barrier,
      flooded: masks?.flooded,
      componentLabels: labels,
      inverseAlignMatrix: alignment.inverseAlignMatrix,
    });
  };

  const meshCtx = (grid: VoxelGrid, voxelDebug?: BandVoxelDebugLayers) => ({
    filledSDF: grid,
    trimProfile,
    resolved,
    alignment,
    timings,
    trimStats,
    gapFillSteps,
    voxelDebug,
  });

  // 'envelope' = the closed band (before the gingival rim is opened).
  if (resolved.debugStage === 'envelope') {
    return finalizeMesh(
      extractAndSmoothBand(bandGrid, resolved),
      meshCtx(bandGrid, buildVoxelDebug(bandGrid)),
    );
  }

  // Gingival trim: clip the band to the crown side of the trim-curve height map (plus the
  // mesh post-clamp above/below). The curtain is extruded along the INSERTION axis (aligned
  // ±Z) — see `occlusalAxis` above — so the rim sits exactly on the curve when the jaw is
  // inspected looking along Z. The gingival sign is picked by pointing the axis away from
  // the tooth solid's centroid.
  const trimCentroid = centroid(trimProfile.points);
  const solidCentroid = insideCentroid(filledSDF);
  const gingivalDir = occlusalAxis.clone();
  if (solidCentroid && solidCentroid.clone().sub(trimCentroid).dot(occlusalAxis) > 0) {
    gingivalDir.negate(); // crowns lie along +occlusalAxis ⇒ gingiva is the other way
  }
  const cutDepth = Math.hypot(
    (filledSDF.info.counts.x - 1) * filledSDF.info.cellSize,
    (filledSDF.info.counts.y - 1) * filledSDF.info.cellSize,
    (filledSDF.info.counts.z - 1) * filledSDF.info.cellSize,
  );
  const cut = applyTrimCut(bandGrid, trimProfile, {
    extrudeDir: gingivalDir,
    depth: cutDepth,
    // Lateral margin for the height-map bounds + debug sheet extent: how far the band wall
    // sits outside the trim curve. The carve itself no longer dilates (the height map
    // extrapolates the nearest margin's height outward).
    outwardMm: resolved.shellOffset + effectiveThickness,
    occlusalLiftMm: resolved.trimCutLiftMm,
  });
  // Drop small disconnected band fragments left near the cut edge (floaters the lift can't
  // reach because they're not part of the main shell component). Conservative thresholds so
  // legitimate per-tooth coverage (a large fraction of the shell) is never removed.
  const bandPrune = pruneInsideComponents(cut.grid, { minVoxels: 128, minFractionOfLargest: 0.02 });
  const openBandGrid = bandPrune.grid;
  trimStats.belowCutModified = cut.removedVoxels + bandPrune.removedVoxels;
  mark('trim-cut');
  logAligner('trim-cut', {
    applied: cut.applied,
    shellIsoMm: round(shellIso),
    trimPoints: trimProfile.points.length,
    outwardMm: round(cut.outwardMm),
    occlusalLiftMm: round(resolved.trimCutLiftMm),
    occlusalAxis: occlusalAxis.toArray().map((c: number) => round(c, 3)),
    gingivalDir: gingivalDir.toArray().map((c: number) => round(c, 3)),
    cutDepthMm: round(cutDepth),
    bandVoxelsRemovedByCut: cut.removedVoxels,
    bandFragmentsPruned: bandPrune.removedComponents,
    bandVoxelsPruned: bandPrune.removedVoxels,
    bandVoxelsAfter: openBandGrid.count((v) => v < 0),
  });

  // Cut-solid debug overlay (only when band-voxel debug is on): hand the solid back to
  // the viewer in world space so the user can see the dilated/dropped trim loop. Else
  // dispose it here.
  let debugCutGeometry: BufferGeometry | undefined;
  if (resolved.showBandVoxelDebug && cut.geometry) {
    debugCutGeometry = cut.geometry.clone();
    debugCutGeometry.applyMatrix4(alignment.inverseAlignMatrix);
  }
  cut.geometry?.dispose();

  const shellGeometry = extractAndSmoothBand(openBandGrid, resolved);
  // Rim finishing (AFTER the global smoother, which pins the boundary):
  //   1. smoothShellRim — Taubin-smooth the rim outline as a 1D loop + blend a thin band of
  //      wall above it (removes the lateral voxel zig-zag the pinned smoother leaves).
  //   2. projectOntoCut — snap the smoothed boundary loop exactly onto the cut surface.
  //   3. clampGeometry — one-sided height clamp for everything else (band vertices, MC
  //      crease chamfer, smoothing sag), so nothing ends below the trim height.
  let rimBoundaryVertices = 0;
  let rimVerticesClamped = 0;
  if (resolved.trimRimClamp) {
    const { boundaryIndices } = smoothShellRim(shellGeometry);
    rimBoundaryVertices = boundaryIndices.length;
    cut.projectOntoCut?.(shellGeometry, boundaryIndices);
    rimVerticesClamped = cut.clampGeometry?.(shellGeometry) ?? 0;
  }
  mark('shell');
  logAligner('shell', {
    innerIsoMm: innerIso,
    outerIsoMm: outerIso,
    shellIsoMm: round(shellIso),
    smoothIterations: resolved.smoothIterations,
    rimClamp: resolved.trimRimClamp,
    rimBoundaryVertices,
    rimVerticesClamped,
    vertices: shellGeometry.getAttribute('position').count,
  });

  // 'shell' (debug) and 'final' share the same geometry; finalizeMesh transforms
  // it back to world space.
  const result = finalizeMesh(shellGeometry, meshCtx(openBandGrid, buildVoxelDebug(bandGrid)));
  result.debugCutGeometry = debugCutGeometry;
  return result;
}

/**
 * Combine two voxel-aligned fields into a signed distance to the *real* (uncapped)
 * tooth surface: magnitude from `uncappedDistance` (unsigned distance to the real
 * crowns), sign from `cappedSigned` (negative inside the sealed capped solid).
 *
 * The result crosses zero on the real tooth surface and reports a large magnitude in
 * the synthetic-cap region (where the nearest real tooth is far), so the downstream
 * shell band never centers on the cap and instead wraps naturally around the crown's
 * lower edge. Both grids must share the same lattice (origin / cellSize / counts).
 */
function rebaseDistanceOntoUncapped(
  cappedSigned: VoxelGrid,
  uncappedDistance: VoxelGrid,
): VoxelGrid {
  const combined = new VoxelGrid(cappedSigned.info);
  const out = combined.data;
  const capped = cappedSigned.data;
  const dist = uncappedDistance.data;
  for (let i = 0; i < out.length; i++) {
    out[i] = capped[i] < 0 ? -dist[i] : dist[i];
  }
  return combined;
}

/** Mean position of a point set. */
function centroid(points: Vector3[]): Vector3 {
  const c = new Vector3();
  for (const p of points) c.add(p);
  if (points.length) c.multiplyScalar(1 / points.length);
  return c;
}

/**
 * Centroid of the inside (sdf < 0) region of a grid, in aligned world space (null if empty).
 * Sub-sampled on a stride-2 lattice (~1/8 the voxels): the centroid is only used to pick the
 * gingival direction's sign, so the tiny shift from sub-sampling is irrelevant.
 */
function insideCentroid(grid: VoxelGrid): Vector3 | null {
  const { counts, origin, cellSize } = grid.info;
  const stride = 2;
  const c = new Vector3();
  let count = 0;
  for (let x = 0; x < counts.x; x += stride) {
    for (let y = 0; y < counts.y; y += stride) {
      for (let z = 0; z < counts.z; z += stride) {
        if (grid.data[grid.index(x, y, z)] < 0) {
          c.x += origin.x + x * cellSize;
          c.y += origin.y + y * cellSize;
          c.z += origin.z + z * cellSize;
          count++;
        }
      }
    }
  }
  if (count === 0) return null;
  return c.multiplyScalar(1 / count);
}

/**
 * Wall-band SDF: negative inside the band {|d - iso| <= halfWidth} centered on the `iso`
 * isosurface, positive on both the inner-cavity and outer sides. With halfWidth = thickness/2
 * this is the full shell wall; marching cubes at iso 0 yields a closed shell of thickness
 * 2·halfWidth (= the requested wall thickness).
 */
function buildWallBandGrid(grid: VoxelGrid, iso: number, halfWidthMm: number): VoxelGrid {
  return grid.map((v) => Math.abs(v - iso) - halfWidthMm);
}

/** Marching cubes on the band + weld + Taubin smooth (rim pinned by the smoother). */
function extractAndSmoothBand(bandGrid: VoxelGrid, resolved: ResolvedParams): BufferGeometry {
  // Normals are skipped here — they'd be computed on the unwelded triangle soup and thrown
  // away; the smoother / finalizeMesh recompute them on the welded mesh.
  let mesh = extractMesh(bandGrid, { isoValue: 0, invertSign: true, computeNormals: false });
  mesh = weldMeshVertices(mesh, resolved.voxelSize * 0.25);
  mesh.computeVertexNormals();
  if (resolved.smoothIterations > 0) {
    // Cap each smoothing step so the two faces of a thin wall can never be pushed through
    // each other (mirrors the wall-resolution floor of 3 voxels used for the band build).
    const effectiveThickness = Math.max(resolved.thickness, 3 * resolved.voxelSize);
    mesh = smoothSurface(mesh, resolved.smoothIterations, 0.5, -0.53, effectiveThickness / 6);
  }
  return mesh;
}

function resolveParams(params: AlignerShellParams): ResolvedParams {
  if (!params.insertionDir) {
    throw new Error('generateAlignerShell: insertionDir is required.');
  }
  if (!params.trimCurve || params.trimCurve.length < 3) {
    throw new Error('generateAlignerShell: trimCurve must contain at least 3 points.');
  }

  const rawVoxel = positive(params.voxelSize, DEFAULTS.voxelSize, 0.05);
  const thickness = positive(params.thickness, DEFAULTS.thickness, 0.05);
  // Shell-band marching cubes needs the wall to be ≥ ~3 voxels thick or it tears
  // into holes. Clamp the voxel so the wall is always resolved regardless of the
  // requested (possibly too-coarse) voxel size.
  const voxelSize = Math.min(rawVoxel, thickness / 3);
  const shellOffset = positive(params.shellOffset, DEFAULTS.shellOffset, 0);
  const blockoutStrength = clamp01(params.blockoutStrength ?? DEFAULTS.blockoutStrength);
  return {
    insertionDir: params.insertionDir.clone().normalize(),
    voxelSize,
    gridPadding: positive(params.gridPadding, DEFAULTS.gridPadding, 0),
    shellOffset,
    thickness,
    gapFillRadius: positive(params.gapFillRadius, DEFAULTS.gapFillRadius, 0),
    blockoutStrength,
    smoothIterations: Math.max(
      0,
      Math.floor(params.smoothIterations ?? DEFAULTS.smoothIterations),
    ),
    trimCutLiftMm: nonNegative(params.trimCutLiftMm, 0),
    trimRimClamp: params.trimRimClamp ?? true,
    trimCurve: params.trimCurve.map((p) => p.clone()),
    // Allow 0/negative: 0 cuts at the margin, negative cuts slightly above it
    // (clears the tooth-cap ring). Only fall back when not a finite number.
    trimMargin: resolveTrimMargin(params.trimMargin),
    maxVoxelCount: Math.max(1000, Math.floor(params.maxVoxelCount ?? DEFAULTS.maxVoxelCount)),
    debugStage: params.debugStage ?? 'final',
    showBandVoxelDebug: params.showBandVoxelDebug ?? false,
    quiet: params.quiet ?? false,
  };
}

function positive(value: number | undefined, fallback: number, min: number) {
  return typeof value === 'number' && Number.isFinite(value) && value > min ? value : fallback;
}

function nonNegative(value: number | undefined, fallback: number) {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : fallback;
}

function resolveTrimMargin(value: number | undefined): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : DEFAULTS.trimMargin;
}

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(1, value));
}

function buildInsertionAlignment(insertionDir: Vector3): AlignmentContext {
  const alignQuat = new Quaternion().setFromUnitVectors(insertionDir.clone().normalize(), INSERTION_AXIS);
  const alignMatrix = new Matrix4().makeRotationFromQuaternion(alignQuat);
  const inverseAlignMatrix = alignMatrix.clone().invert();
  return { alignMatrix, inverseAlignMatrix };
}

function alignMeshes(meshes: Mesh[], alignMatrix: Matrix4): Mesh[] {
  const aligned: Mesh[] = [];
  for (const mesh of meshes) {
    if (!mesh?.geometry) continue;
    mesh.updateMatrixWorld(true);
    const geometry = mesh.geometry.clone();
    geometry.applyMatrix4(mesh.matrixWorld);
    geometry.applyMatrix4(alignMatrix);
    const clone = new Mesh(geometry);
    clone.matrix.identity();
    clone.updateMatrixWorld(true);
    aligned.push(clone);
  }
  if (!aligned.length) {
    throw new Error('generateAlignerShell: no valid capped meshes after alignment.');
  }
  return aligned;
}

function unionMeshBounds(meshes: Mesh[]): Box3 {
  const bounds = new Box3();
  for (const mesh of meshes) {
    mesh.geometry.computeBoundingBox();
    if (mesh.geometry.boundingBox) bounds.union(mesh.geometry.boundingBox);
  }
  return bounds;
}

// Smallest voxel size >= the requested one whose predicted grid fits within maxVoxelCount.
// The grid cell count grows ~1/voxel^3, so a cube-root scale lands close; we then nudge upward
// to absorb the padding term (itself voxel-dependent) and the per-axis ceil()+1 rounding. The
// prediction mirrors VoxelGrid.fromBounds exactly, so the post-build assertVoxelBudget guard
// will agree.
function fitVoxelSizeToBudget(
  bounds: Box3,
  requestedVoxelSize: number,
  paddingFor: (voxelSize: number) => number,
  maxVoxelCount: number,
): number {
  const extent = bounds.getSize(new Vector3());
  const predictTotal = (voxel: number) => {
    const pad = paddingFor(voxel);
    const nx = Math.ceil((extent.x + 2 * pad) / voxel) + 1;
    const ny = Math.ceil((extent.y + 2 * pad) / voxel) + 1;
    const nz = Math.ceil((extent.z + 2 * pad) / voxel) + 1;
    return nx * ny * nz;
  };
  const initialTotal = predictTotal(requestedVoxelSize);
  if (initialTotal <= maxVoxelCount) return requestedVoxelSize;
  let voxel = requestedVoxelSize * Math.cbrt(initialTotal / maxVoxelCount);
  for (let i = 0; i < 24 && predictTotal(voxel) > maxVoxelCount; i++) {
    voxel *= 1.03;
  }
  return voxel;
}

function assertVoxelBudget(grid: VoxelGrid, maxVoxelCount: number) {
  if (grid.info.total > maxVoxelCount) {
    const { counts, total } = grid.info;
    throw new Error(
      `generateAlignerShell: voxel grid too large (${counts.x}x${counts.y}x${counts.z}=${total}). Increase voxelSize or reduce padding.`,
    );
  }
}

function emptyStats(heightMapCells: number): TrimFieldStats {
  return {
    belowCutModified: 0,
    insertionOpenModified: 0,
    gumBelowModified: 0,
    heightMapCells,
  };
}

type FinalizeSdfCtx = {
  filledSDF: VoxelGrid;
  trimProfile: TrimProfileCurve;
  resolved: ResolvedParams;
  alignment: AlignmentContext;
  timings: Record<string, number>;
  trimStats: TrimFieldStats;
  gapFillSteps: number;
};

function finalizeSdf(resultSDF: VoxelGrid, ctx: FinalizeSdfCtx): AlignerShellResult {
  let geometry = extractMesh(resultSDF, { isoValue: 0, invertSign: true, computeNormals: false });
  geometry = weldMeshVertices(geometry, ctx.resolved.voxelSize * 0.25);
  return finalizeMesh(geometry, ctx);
}

function finalizeMesh(
  geometry: BufferGeometry,
  ctx: {
    filledSDF: VoxelGrid;
    trimProfile: TrimProfileCurve;
    resolved: ResolvedParams;
    alignment: AlignmentContext;
    timings: Record<string, number>;
    trimStats: TrimFieldStats;
    gapFillSteps: number;
    voxelDebug?: BandVoxelDebugLayers;
  },
): AlignerShellResult {
  geometry.applyMatrix4(ctx.alignment.inverseAlignMatrix);
  geometry.computeVertexNormals();
  return buildResult(
    geometry,
    ctx.filledSDF,
    ctx.trimProfile,
    ctx.resolved,
    ctx.timings,
    ctx.trimStats,
    ctx.gapFillSteps,
    ctx.voxelDebug,
  );
}

function buildResult(
  geometry: BufferGeometry,
  filledSDF: VoxelGrid,
  trimProfile: TrimProfileCurve,
  resolved: ResolvedParams,
  timingsMs: Record<string, number>,
  trimStats: TrimFieldStats,
  gapFillSteps: number,
  voxelDebug?: BandVoxelDebugLayers,
): AlignerShellResult {
  const positionAttr = geometry.getAttribute('position');
  const indexAttr = geometry.getIndex();
  const triangleCount = indexAttr ? indexAttr.count / 3 : positionAttr.count / 3;
  const carvedVoxels =
    trimStats.belowCutModified +
    trimStats.insertionOpenModified +
    trimStats.gumBelowModified;

  logAligner('RESULT', {
    stage: resolved.debugStage,
    timingsMs,
    triangleCount,
    vertexCount: positionAttr.count,
    carvedVoxels,
    gapFillSteps,
    blockoutStrength: resolved.blockoutStrength,
    trimAverageZ: trimProfile.defaultZ,
  });
  logTimingSummary(timingsMs);

  return {
    surfaceGeometry: geometry,
    voxelDebug,
    metadata: {
      grid: {
        counts: filledSDF.info.counts,
        voxelSize: filledSDF.info.cellSize,
        total: filledSDF.info.total,
        bounds: {
          min: filledSDF.info.bounds.min.toArray() as [number, number, number],
          max: filledSDF.info.bounds.max.toArray() as [number, number, number],
        },
      },
      shell: {
        clearance: resolved.shellOffset,
        thickness: resolved.thickness,
        gapFillSteps,
        shellVoxels: filledSDF.count((value) => value < 0),
      },
      trim: {
        curvePoints: resolved.trimCurve.length,
        averageZ: trimProfile.defaultZ,
        carvedVoxels,
        heightMapCells: trimStats.heightMapCells,
        belowCutModified: trimStats.belowCutModified,
        insertionOpenModified: trimStats.insertionOpenModified,
        clipRemovedTriangles: 0,
      },
      debug: {
        stage: resolved.debugStage,
        timingsMs,
      },
      mesh: { vertexCount: positionAttr.count, triangleCount },
    },
  };
}

function createTimer(timingsMs: Record<string, number>) {
  let previous = performance.now();
  return (label: string) => {
    const now = performance.now();
    timingsMs[label] = now - previous;
    previous = now;
  };
}

let logQuiet = false;

function logAligner(step: string, payload: Record<string, unknown>) {
  if (logQuiet) return;
  console.info(`${LOG_PREFIX} ${step}`, payload);
}

/** Per-stage timing breakdown (ms + % of total), sorted slowest-first, with the total. */
function logTimingSummary(timingsMs: Record<string, number>) {
  if (logQuiet) return;
  const entries = Object.entries(timingsMs);
  const total = entries.reduce((sum, [, ms]) => sum + ms, 0);
  const breakdown = entries
    .sort((a, b) => b[1] - a[1])
    .map(([stage, ms]) => ({
      stage,
      ms: round(ms),
      pct: total > 0 ? Number(((100 * ms) / total).toFixed(1)) : 0,
    }));
  console.info(`${LOG_PREFIX} TIMING SUMMARY — total ${round(total)} ms`, breakdown);
}

function summarizeSdfRange(grid: VoxelGrid) {
  let min = Infinity;
  let max = -Infinity;
  for (const value of grid.data) {
    min = Math.min(min, value);
    max = Math.max(max, value);
  }
  return { min, max };
}

function logSdf(step: string, grid: VoxelGrid, extra: Record<string, unknown> = {}) {
  const inside = grid.count((v) => v < 0);
  const total = grid.info.total;
  const { min, max } = summarizeSdfRange(grid);
  logAligner(step, {
    ...extra,
    grid: `${grid.info.counts.x}x${grid.info.counts.y}x${grid.info.counts.z}`,
    voxelMm: grid.info.cellSize,
    insideVoxels: inside,
    insidePct: `${((100 * inside) / total).toFixed(1)}%`,
    sdfMinMax: { min: round(min), max: round(max) },
  });
}

function round(value: number, digits = 3): number {
  return Number(value.toFixed(digits));
}
